import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Boxes, FilePlus2, Lock, Wallet } from 'lucide-react';

import { lotPaymentsAPI, urlImpressionDecompte } from '../../lib/lotPaymentsApi';
import { formatDate, formatPrixUnitaire } from '../../lib/formats';
import { messageErreur } from '../../lib/erreurs';
import { texteSur } from '../../lib/libelles';
import { useSyncOuEnLigne } from '../../context/SyncContext';
import { useRafraichirSur } from '../../hooks/useRafraichirSur';
import { useDroitsLots } from '../../hooks/useDroitsLots';
import { PageHeader } from '../../components/ui/page-header';
import { PrintMenu } from '../../components/business/PrintMenu';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { StatutBadge } from '../../components/ui/StatutBadge';
import { EmptyState } from '../../components/ui/empty-state';
import { LoadingState } from '../../components/ui/loading-state';
import { toast } from '../../components/ui/sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import AcompteDialog from '../../components/lots/AcompteDialog';
import {
  CelluleEntier,
  CelluleMontant,
  StatutDecompte,
  StatutPaiement,
  TuilesSituation,
} from '../../components/lots/paiementsUi';

const CADRE = 'mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8';

/**
 * Le paiement d'un lot : sa situation, les acomptes versés au partenaire et les
 * décomptes (un décompte règle des camions dépotés). Réservé à qui voit les montants.
 */
const LotPaiementsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { peutEcrire, voitArgent } = useDroitsLots();
  // L'argent attend le serveur : boutons inactifs sans lui.
  const enLigne = useSyncOuEnLigne().isOnline !== false;

  const [donnees, setDonnees] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [introuvable, setIntrouvable] = useState(false);
  const [erreur, setErreur] = useState(false);
  const [dialogue, setDialogue] = useState(false);

  const charger = useCallback(
    async ({ silencieux = false } = {}) => {
      if (!silencieux) setChargement(true);
      try {
        setDonnees(await lotPaymentsAPI.payments(id, { skipToast: true }));
        setIntrouvable(false);
        setErreur(false);
      } catch (err) {
        console.error('Failed to load lot payments:', err);
        if (err?.response?.status === 404) setIntrouvable(true);
        else setErreur(true);
        if (!silencieux && err?.response?.status !== 404) {
          toast.error(messageErreur(err, 'Impossible de charger les paiements du lot'));
        }
      } finally {
        setChargement(false);
      }
    },
    [id],
  );

  useEffect(() => {
    setDonnees(null);
    if (voitArgent) charger();
  }, [charger, voitArgent]);

  useRafraichirSur(['lots', 'finance'], charger, { actif: voitArgent });

  const lot = donnees?.lot;
  const situation = donnees?.situation;
  const acomptes = useMemo(() => donnees?.advances || [], [donnees]);
  const decomptes = useMemo(() => donnees?.settlements || [], [donnees]);

  if (!voitArgent) {
    return (
      <div className={CADRE} data-testid="lot-paiements">
        <PageHeader eyebrow="Lots attribués" title="Paiements" backTo="/lots/paiements" backLabel="Paiements" />
        <EmptyState variant="error" icon={Lock} title="Ces montants sont réservés à la direction" />
      </div>
    );
  }

  if (chargement && !donnees) {
    return (
      <div className={CADRE}>
        <LoadingState shape="detail" />
      </div>
    );
  }

  if (!lot) {
    return (
      <div className={CADRE}>
        <PageHeader eyebrow="Lots attribués" title="Paiements" backTo="/lots/paiements" backLabel="Paiements" />
        <EmptyState
          variant="error"
          icon={Boxes}
          title={introuvable ? 'Ce lot est introuvable' : 'Les paiements du lot n’ont pas pu être chargés'}
          actionLabel={introuvable ? 'Retour aux paiements' : erreur ? 'Réessayer' : undefined}
          onAction={introuvable ? () => navigate('/lots/paiements') : () => charger()}
        />
      </div>
    );
  }

  const devise = lot.currency || situation?.currency || 'XOF';
  const prixAbsent = lot.price_per_liter === null || lot.price_per_liter === undefined;
  const ecritureBloquee = !peutEcrire || prixAbsent || !enLigne;

  return (
    <div className={CADRE} data-testid="lot-paiements">
      <PageHeader
        eyebrow="Lots attribués"
        title={lot.reference || 'Lot'}
        backTo="/lots/paiements"
        backLabel="Paiements"
        meta={
          <>
            <StatutBadge domaine="lot_statut" code={lot.status} />
            <StatutPaiement code={situation?.payment_status} />
            {lot.transporter_name ? <span>{lot.transporter_name}</span> : null}
            {!prixAbsent ? (
              <span className="tabular-nums" data-testid="lot-paiements-prix">
                {formatPrixUnitaire(lot.price_per_liter, devise)}/L
              </span>
            ) : null}
          </>
        }
        stats={[
          { label: 'Confirmés', valeur: Number(situation?.confirmed_trucks) || 0, testid: 'lot-paiements-confirmes' },
          { label: 'Dépotés', valeur: Number(situation?.unloaded_trucks) || 0, testid: 'lot-paiements-depotes' },
          {
            label: 'Décomptés',
            valeur: Number(situation?.settled_trucks) || 0,
            ton: 'positif',
            testid: 'lot-paiements-decomptes',
          },
        ]}
        alertes={
          prixAbsent
            ? [
                {
                  niveau: 'attention',
                  court: 'prix au litre à saisir',
                  texte: 'Le prix au litre du lot n’est pas saisi : aucun paiement n’est possible.',
                  action: { label: 'Ouvrir le lot', onClick: () => navigate(`/lots/${id}`) },
                },
              ]
            : []
        }
        actions={
          <>
            <Button variant="outline" onClick={() => navigate(`/lots/${id}`)} data-testid="lot-paiements-voir-lot">
              <Boxes className="h-4 w-4" aria-hidden="true" />
              Voir le lot
            </Button>
            {peutEcrire ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => setDialogue(true)}
                  disabled={ecritureBloquee}
                  data-testid="lot-paiements-acompte"
                >
                  <Wallet className="h-4 w-4" aria-hidden="true" />
                  Verser un acompte
                </Button>
                <Button
                  onClick={() => navigate(`/lots/${id}/decomptes/nouveau`)}
                  disabled={ecritureBloquee}
                  data-testid="lot-paiements-nouveau-decompte"
                >
                  <FilePlus2 className="h-4 w-4" aria-hidden="true" />
                  Nouveau décompte
                </Button>
              </>
            ) : null}
          </>
        }
      />

      <TuilesSituation situation={situation} testid="lot-paiements-tuiles" />

      <section className="space-y-2" aria-label="Acomptes" data-testid="lot-paiements-acomptes">
        <h2 className="text-sm font-bold text-foreground">Acomptes</h2>
        {acomptes.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border px-4 py-3 text-sm text-muted-foreground">
            Aucun acompte versé
          </p>
        ) : (
          <Card className="card-base">
            <CardContent className="p-0">
              <Table compact stickyFirstColumn={false}>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Observations</TableHead>
                    <TableHead className="text-right">Montant</TableHead>
                    <TableHead className="text-right">Imputé</TableHead>
                    <TableHead className="text-right">Remboursé</TableHead>
                    <TableHead className="text-right">Disponible</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {acomptes.map((acompte) => (
                    <TableRow key={acompte.payment_id} data-testid={`lot-acompte-${acompte.payment_id}`}>
                      <TableCell className="whitespace-nowrap tabular-nums">{formatDate(acompte.date)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{texteSur(acompte.note, '')}</TableCell>
                      <CelluleMontant valeur={acompte.amount} devise={devise} />
                      <CelluleMontant valeur={acompte.allocated} devise={devise} discret />
                      <CelluleMontant valeur={acompte.refunded} devise={devise} discret />
                      <CelluleMontant valeur={acompte.available} devise={devise} gras />
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </section>

      <section className="space-y-2" aria-label="Décomptes" data-testid="lot-paiements-decomptes-section">
        <h2 className="text-sm font-bold text-foreground">Décomptes</h2>
        {decomptes.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border px-4 py-3 text-sm text-muted-foreground">
            Aucun décompte
          </p>
        ) : (
          <Card className="card-base">
            <CardContent className="p-0">
              <Table compact stickyFirstColumn={false} data-testid="lot-paiements-table-decomptes">
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <span className="block">Référence</span>
                      <span className="block">Établi le</span>
                    </TableHead>
                    <TableHead className="text-right">Camions</TableHead>
                    <TableHead className="text-right">Montant</TableHead>
                    <TableHead className="text-right">Retenues</TableHead>
                    <TableHead className="text-right">Solde</TableHead>
                    <TableHead className="text-right">Acomptes</TableHead>
                    <TableHead className="text-right">Net</TableHead>
                    <TableHead>
                      <span className="block">Statut</span>
                      <span className="block">Paiement</span>
                    </TableHead>
                    <TableHead className="text-right">Reste</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {decomptes.map((decompte) => {
                    const valide = decompte.status === 'VALIDE';
                    const annule = decompte.status === 'ANNULE';
                    const monnaie = decompte.currency || devise;
                    return (
                      <TableRow
                        key={decompte.id}
                        className="cursor-pointer"
                        onClick={() => navigate(`/lots/${id}/decomptes/${decompte.id}`)}
                        data-testid={`lot-decompte-${decompte.id}`}
                      >
                        <TableCell className="whitespace-nowrap">
                          <span className="inline-flex flex-col leading-tight">
                            <span className="font-mono font-semibold">{decompte.reference || 'Brouillon'}</span>
                            <span className="text-xs tabular-nums text-muted-foreground">{formatDate(decompte.issued_at)}</span>
                          </span>
                        </TableCell>
                        <CelluleEntier valeur={decompte.lines_count} />
                        <CelluleMontant valeur={decompte.total_amount} devise={monnaie} discret={annule} />
                        <CelluleMontant valeur={decompte.total_retention} devise={monnaie} discret={annule} />
                        <CelluleMontant valeur={decompte.total_balance} devise={monnaie} discret={annule} />
                        <CelluleMontant valeur={decompte.advances_total} devise={monnaie} discret={annule} />
                        <CelluleMontant valeur={decompte.net_amount} devise={monnaie} gras discret={annule} />
                        <TableCell>
                          <span className="inline-flex flex-col items-start gap-1">
                            <StatutDecompte code={decompte.status} />
                            {valide && decompte.payment_status ? <StatutPaiement code={decompte.payment_status} /> : null}
                          </span>
                        </TableCell>
                        {valide ? (
                          <CelluleMontant valeur={decompte.bill_remaining} devise={monnaie} />
                        ) : (
                          <TableCell className="text-right text-muted-foreground">—</TableCell>
                        )}
                        <TableCell className="text-right" onClick={(evenement) => evenement.stopPropagation()}>
                          {valide ? (
                            <PrintMenu
                              label="Imprimer"
                              variant="ghost"
                              items={[
                                {
                                  label: decompte.reference || 'Décompte',
                                  url: urlImpressionDecompte(id, decompte.id),
                                  filename: `decompte-${decompte.reference || decompte.id}`,
                                  formats: ['pdf', 'xlsx'],
                                },
                              ]}
                            />
                          ) : null}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </section>

      {erreur && donnees ? (
        <p className="text-xs text-destructive" role="alert">
          Les chiffres n’ont pas pu être actualisés.
        </p>
      ) : null}

      {peutEcrire ? (
        <AcompteDialog
          open={dialogue}
          onOpenChange={setDialogue}
          lotId={id}
          reference={lot.reference}
          devise={devise}
          onDone={() => charger({ silencieux: true })}
        />
      ) : null}
    </div>
  );
};

export default LotPaiementsPage;
